import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import SimplePDFViewer from '../../components/SimplePDFViewer';
import { extensionOf, viewerKindOf } from './documentTypes';

const MAX_SHEET_ROWS = 2000;

// docx-preview and pptx-preview turn the file into live DOM. A hyperlink in an
// uploaded file could carry a javascript: URL, so anything that is not an
// ordinary web/mail link loses its href.
const neutralizeLinks = (container) => {
  container.querySelectorAll('a[href]').forEach(anchor => {
    const href = anchor.getAttribute('href') || '';
    if (!/^(https?:|mailto:|#)/i.test(href.trim())) anchor.removeAttribute('href');
    else if (!href.startsWith('#')) {
      anchor.setAttribute('target', '_blank');
      anchor.setAttribute('rel', 'noopener noreferrer');
    }
  });
};

const DocxView = ({ blob, onFail }) => {
  const ref = useRef(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { renderAsync } = await import('docx-preview');
        if (cancelled || !ref.current) return;
        ref.current.innerHTML = '';
        await renderAsync(blob, ref.current, undefined, {
          inWrapper: true,
          breakPages: true,
          ignoreLastRenderedPageBreak: true,
          experimental: true,
          // altChunks embed raw HTML from the file; never render that.
          renderAltChunks: false,
        });
        if (!cancelled) neutralizeLinks(ref.current);
      } catch (error) {
        console.error('Rendering DOCX failed:', error);
        if (!cancelled) onFail();
      }
    })();
    return () => { cancelled = true; };
  }, [blob, onFail]);
  return <div ref={ref} className="dd-docx" dir="auto" />;
};

const PptxView = ({ blob, onFail }) => {
  const ref = useRef(null);
  useEffect(() => {
    let cancelled = false;
    let previewer = null;
    (async () => {
      try {
        const [{ init }, buffer] = await Promise.all([import('pptx-preview'), blob.arrayBuffer()]);
        if (cancelled || !ref.current) return;
        ref.current.innerHTML = '';
        const width = Math.min(ref.current.clientWidth || 960, 1100);
        previewer = init(ref.current, { width, height: Math.round(width * 9 / 16), mode: 'list' });
        await previewer.preview(buffer);
        if (!cancelled) neutralizeLinks(ref.current);
      } catch (error) {
        console.error('Rendering PPTX failed:', error);
        if (!cancelled) onFail();
      }
    })();
    return () => {
      cancelled = true;
      try { previewer?.destroy(); } catch { /* already gone */ }
    };
  }, [blob, onFail]);
  return <div ref={ref} className="dd-pptx" />;
};

// Spreadsheets render as plain React tables (never injected HTML), one tab per sheet.
const SheetView = ({ blob, fileName, onFail }) => {
  const [sheets, setSheets] = useState(null);
  const [active, setActive] = useState(0);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const XLSX = await import('xlsx');
        const workbook = extensionOf(fileName) === 'csv'
          ? XLSX.read(await blob.text(), { type: 'string' })
          : XLSX.read(await blob.arrayBuffer(), { type: 'array' });
        const parsed = workbook.SheetNames.map(name => ({
          name,
          rows: XLSX.utils.sheet_to_json(workbook.Sheets[name], { header: 1, defval: '', raw: false }),
        }));
        if (!cancelled) setSheets(parsed);
      } catch (error) {
        console.error('Reading spreadsheet failed:', error);
        if (!cancelled) onFail();
      }
    })();
    return () => { cancelled = true; };
  }, [blob, fileName, onFail]);

  if (!sheets) return <div className="pdf-loading">טוען...</div>;
  const sheet = sheets[active] || { rows: [] };
  const rows = sheet.rows.slice(0, MAX_SHEET_ROWS);
  return (
    <div className="dd-sheet">
      {sheets.length > 1 && (
        <div className="dd-sheet-tabs">
          {sheets.map((s, i) => (
            <button key={s.name} type="button" className={i === active ? 'active' : ''} onClick={() => setActive(i)}>
              {s.name}
            </button>
          ))}
        </div>
      )}
      <div className="dd-sheet-scroll">
        <table dir="auto">
          <tbody>
            {rows.map((row, r) => (
              <tr key={r}>
                {row.map((cell, c) => <td key={c}>{String(cell)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sheet.rows.length > MAX_SHEET_ROWS && (
        <p className="dd-sheet-note">מוצגות {MAX_SHEET_ROWS} השורות הראשונות. להצגת הקובץ המלא יש להוריד אותו.</p>
      )}
    </div>
  );
};

const TextView = ({ blob }) => {
  const [text, setText] = useState('');
  useEffect(() => { blob.text().then(setText); }, [blob]);
  return <pre className="dd-text" dir="auto">{text}</pre>;
};

const NoPreview = ({ fileName, message, onDownload }) => (
  <div className="file-viewer-placeholder">
    <p>{message || 'תצוגה מקדימה אינה זמינה עבור סוג קובץ זה'}</p>
    <p>שם הקובץ: {fileName}</p>
    <button type="button" onClick={onDownload}>הורד קובץ</button>
  </div>
);

/**
 * Shows a department document in the page, picking a viewer by extension. Every
 * viewer works from the Blob already fetched through the API, so nothing is sent
 * to a third-party service. Formats no viewer handles (legacy .doc/.ppt, .rtf,
 * mail, TIFF, AVI) and files a viewer fails on fall back to a download.
 */
const DocumentRenderer = ({ blob, fileName, onDownload }) => {
  const [failed, setFailed] = useState(false);
  // Created and revoked by the same effect. A useMemo'd URL revoked in an
  // effect cleanup breaks under StrictMode's mount-unmount-mount: the memo
  // survives, the URL it holds does not, and pdf.js gets ERR_FILE_NOT_FOUND.
  const [objectUrl, setObjectUrl] = useState(null);
  useEffect(() => {
    const url = URL.createObjectURL(blob);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [blob]);
  const onFail = useMemo(() => () => setFailed(true), []);

  if (failed) {
    return <NoPreview fileName={fileName} message="לא ניתן להציג את הקובץ. ניתן להוריד אותו ולפתוח במחשב." onDownload={onDownload} />;
  }

  const kind = viewerKindOf(fileName);
  if (!objectUrl && ['pdf', 'image', 'video'].includes(kind)) return null;

  switch (kind) {
    case 'pdf':
      return <SimplePDFViewer file={objectUrl} onError={onFail} />;
    case 'image':
      return (
        <div className="image-viewer">
          <img src={objectUrl} alt={fileName} style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }} onError={onFail} />
        </div>
      );
    case 'video':
      return (
        <div className="image-viewer">
          <video src={objectUrl} controls style={{ maxWidth: '100%', maxHeight: '80vh' }} onError={onFail} />
        </div>
      );
    case 'docx':
      return <DocxView blob={blob} onFail={onFail} />;
    case 'pptx':
      return <PptxView blob={blob} onFail={onFail} />;
    case 'sheet':
      return <SheetView blob={blob} fileName={fileName} onFail={onFail} />;
    case 'text':
      return <TextView blob={blob} />;
    default: {
      const ext = extensionOf(fileName);
      const legacyOffice = ext === 'doc' || ext === 'ppt';
      return (
        <NoPreview
          fileName={fileName}
          message={legacyOffice
            ? `לא ניתן להציג קבצי ${ext.toUpperCase()} בפורמט הישן. ניתן להוריד את הקובץ, או לשמור אותו מחדש כ-${ext.toUpperCase()}X ולהעלות שוב.`
            : undefined}
          onDownload={onDownload}
        />
      );
    }
  }
};

const blobProps = { blob: PropTypes.instanceOf(Blob).isRequired };
DocxView.propTypes = { ...blobProps, onFail: PropTypes.func.isRequired };
PptxView.propTypes = { ...blobProps, onFail: PropTypes.func.isRequired };
SheetView.propTypes = { ...blobProps, fileName: PropTypes.string, onFail: PropTypes.func.isRequired };
TextView.propTypes = blobProps;
NoPreview.propTypes = { fileName: PropTypes.string, message: PropTypes.string, onDownload: PropTypes.func.isRequired };
DocumentRenderer.propTypes = { ...blobProps, fileName: PropTypes.string, onDownload: PropTypes.func.isRequired };

export default DocumentRenderer;
